class FloatingFreeShippingPopup {
  constructor() {
    this.popup = document.querySelector(
      '[data-floating-free-shipping-popup]'
    );

    if (!this.popup) return;

    this.textEl = this.popup.querySelector(
      '[data-floating-free-shipping-text]'
    );

    this.progressEl = this.popup.querySelector(
      '[data-floating-free-shipping-progress]'
    );

    this.giftMilestone = this.popup.querySelector(
      '[data-floating-gift-milestone]'
    );

    this.halfShippingMilestone = this.popup.querySelector(
      '[data-floating-half-shipping-milestone]'
    );

    this.shippingMilestone = this.popup.querySelector(
      '[data-floating-shipping-milestone]'
    );

    this.store = this.popup.dataset.store || 'fi';
    this.currency = this.popup.dataset.currency || 'EUR';

    /*
     * Liquid antaa nämä arvot valuutan pienimmän yksikön
     * sijaan kokonaisina valuuttamäärinä, esim:
     *
     * FI: 25 / 60
     * EU EUR: 45 / 70 / 110
     * PLN: 194 / 302 / 474
     *
     * Muutetaan ne senteiksi / groszeiksi jne.
     */
    this.giftThreshold =
      Number(this.popup.dataset.giftThreshold || 0) * 100;

    this.halfShippingThreshold =
      this.store === 'eu'
        ? Number(this.popup.dataset.halfShippingThreshold || 0) * 100
        : null;

    this.shippingThreshold =
      Number(this.popup.dataset.shippingThreshold || 0) * 100;

    this.previousItemCount = null;
    this.hideTimeout = null;

    this.init();
  }

  init() {
    this.refresh(true);

    document.addEventListener('cart:refresh', (event) => {
      if (event.detail?.cart) {
        this.update(event.detail.cart);
      } else {
        this.refresh();
      }
    });

    document.addEventListener('click', (event) => {
      if (
        event.target.closest(
          'button[type="submit"][name="add"]'
        ) ||
        event.target.closest('[name="add"]')
      ) {
        this.waitForCartUpdate();
      }
    });
  }

  async waitForCartUpdate(retries = 6, delay = 400) {
    const previousCount =
      this.previousItemCount ?? 0;

    for (let i = 0; i < retries; i++) {
      await new Promise((resolve) =>
        setTimeout(resolve, delay)
      );

      try {
        const response = await fetch('/cart.js', {
          cache: 'no-store',
        });

        const cart = await response.json();
        const itemCount = Number(cart.item_count || 0);

        if (itemCount > previousCount) {
          this.update(cart);
          return;
        }
      } catch (error) {
        console.error(
          'Waiting for cart update failed:',
          error
        );
      }
    }

    this.refresh();
  }

  async refresh(isInitial = false) {
    if (window.innerWidth >= 750) return;

    try {
      const response = await fetch('/cart.js', {
        cache: 'no-store',
      });

      const cart = await response.json();

      this.update(cart, isInitial);
    } catch (error) {
      console.error(
        'Floating free shipping popup update failed:',
        error
      );
    }
  }

  update(cart, isInitial = false) {
    if (window.innerWidth >= 750) return;

    const itemCount = Number(cart.item_count || 0);

    /*
     * Käytetään total_pricea.
     *
     * Jos automaattinen lahjatuote maksaa 0 €, se ei vaikuta
     * summaan. Jos haluat täysin saman qualifying subtotal
     * -logiikan kuin pää-JS:ssä, voidaan yhdistää nämä myöhemmin.
     */
    const total = Number(cart.total_price || 0);

    const remainingToGift = Math.max(
      this.giftThreshold - total,
      0
    );

    const remainingToHalfShipping =
      this.halfShippingThreshold !== null
        ? Math.max(
            this.halfShippingThreshold - total,
            0
          )
        : 0;

    const remainingToShipping = Math.max(
      this.shippingThreshold - total,
      0
    );

    const progress =
      this.shippingThreshold > 0
        ? Math.min(
            (total / this.shippingThreshold) * 100,
            100
          )
        : 0;

    if (this.progressEl) {
      this.progressEl.style.width = `${progress}%`;
    }

    /*
     * Lahja
     */
    this.updateMilestone(
      this.giftMilestone,
      total >= this.giftThreshold,
      '🎁'
    );

    /*
     * 50 % toimitusetu vain EU:ssa.
     *
     * FI:ssä Liquid ei edes renderöi tätä milestonea.
     */
    if (
      this.store === 'eu' &&
      this.halfShippingMilestone &&
      this.halfShippingThreshold !== null
    ) {
      this.updateMilestone(
        this.halfShippingMilestone,
        total >= this.halfShippingThreshold,
        '🚚'
      );
    }

    /*
     * Ilmainen toimitus
     */
    this.updateMilestone(
      this.shippingMilestone,
      total >= this.shippingThreshold,
      '🚚'
    );

    if (this.textEl) {
      /*
       * FI
       * 0–24,99 € → lahja
       * 25–59,99 € → ilmainen toimitus
       * 60 €+ → kaikki saavutettu
       */
      if (this.store === 'fi') {
        if (total < this.giftThreshold) {
          this.textEl.innerHTML =
            `🎁 Ilmainen kristalli yli ${this.formatMoney(
              this.giftThreshold
            )}<br>` +
            `Lisää vielä <strong>${this.formatMoney(
              remainingToGift
            )}</strong> 💜`;
        } else if (total < this.shippingThreshold) {
          this.textEl.innerHTML =
            `🎁 Ilmainen kristalli lisätty koriin<br>` +
            `Lisää vielä <strong>${this.formatMoney(
              remainingToShipping
            )}</strong> ilmaiseen toimitukseen 🚚`;
        } else {
          this.textEl.innerHTML =
            `🎁 Ilmainen kristalli + ilmainen toimitus aktivoitu 💜`;
        }
      }

      /*
       * EU
       * 0–44,99 € → lahja
       * 45–69,99 € → 50 % toimitusetu
       * 70–109,99 € → ilmainen toimitus
       * 110 €+ → kaikki saavutettu
       */
      else {
        if (total < this.giftThreshold) {
          this.textEl.innerHTML =
            `🎁 Free crystal on orders over ${this.formatMoney(
              this.giftThreshold
            )}<br>` +
            `Add <strong>${this.formatMoney(
              remainingToGift
            )}</strong> more 💜`;
        } else if (
          this.halfShippingThreshold !== null &&
          total < this.halfShippingThreshold
        ) {
          this.textEl.innerHTML =
            `🎁 Free crystal added to your cart<br>` +
            `Add <strong>${this.formatMoney(
              remainingToHalfShipping
            )}</strong> more for 50% off shipping 🚚`;
        } else if (total < this.shippingThreshold) {
          this.textEl.innerHTML =
            `🚚 50% off shipping unlocked<br>` +
            `Add <strong>${this.formatMoney(
              remainingToShipping
            )}</strong> more for free shipping`;
        } else {
          this.textEl.innerHTML =
            `🎁 Free crystal + free shipping unlocked 💜`;
        }
      }
    }

    if (isInitial) {
      this.previousItemCount = itemCount;
      this.hide(true);
      return;
    }

    const itemCountIncreased =
      this.previousItemCount !== null &&
      itemCount > this.previousItemCount;

    if (itemCountIncreased) {
      this.showForThreeSeconds();
    }

    if (itemCount === 0) {
      this.hide(true);
    }

    this.previousItemCount = itemCount;
  }

  updateMilestone(
    milestone,
    isActive,
    inactiveIcon
  ) {
    if (!milestone) return;

    milestone.classList.toggle(
      'is-active',
      isActive
    );

    const dot = milestone.querySelector(
      '.floating-free-shipping-popup__milestone-dot'
    );

    if (dot) {
      dot.textContent =
        isActive
          ? '✓'
          : inactiveIcon;
    }

    /*
     * Labelia EI vaihdeta enää JS:ssä.
     *
     * Liquid on jo renderöinyt oikean:
     * FI 25 / 60
     * EU 45 / 70 / 110
     * SEK / PLN / DKK jne.
     */
  }

  showForThreeSeconds() {
    clearTimeout(this.hideTimeout);

    this.popup.classList.remove('hidden');

    this.popup.setAttribute(
      'aria-hidden',
      'false'
    );

    requestAnimationFrame(() => {
      this.popup.classList.add(
        'is-visible'
      );
    });

    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, 3000);
  }

  hide(force = false) {
    clearTimeout(this.hideTimeout);

    this.popup.classList.remove(
      'is-visible'
    );

    this.popup.setAttribute(
      'aria-hidden',
      'true'
    );

    if (force) {
      this.popup.classList.add(
        'hidden'
      );
      return;
    }

    setTimeout(() => {
      if (
        !this.popup.classList.contains(
          'is-visible'
        )
      ) {
        this.popup.classList.add(
          'hidden'
        );
      }
    }, 250);
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(
        this.getCurrencyLocale(),
        {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      ).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${this.currency}`;
    }
  }

  getCurrencyLocale() {
    const locale =
      document.documentElement.lang ||
      window.Shopify?.locale ||
      'en';

    const language =
      locale.toLowerCase();

    if (!language.startsWith('en')) {
      return language;
    }

    const currencyLocales = {
      EUR: 'en-IE',
      SEK: 'sv-SE',
      DKK: 'da-DK',
      PLN: 'pl-PL',
      CZK: 'cs-CZ',
      HUF: 'hu-HU',
      RON: 'ro-RO',
    };

    return (
      currencyLocales[this.currency] ||
      language
    );
  }
}

window.floatingFreeShippingPopup =
  new FloatingFreeShippingPopup();